import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { fetchAcharyaAudio } from "@/lib/acharya/acharya-service";

function asAudioSrc(payload) {
  if (!payload) return null;
  if (payload.url) return payload.url;
  if (payload.audio_base64 && payload.mime_type) return `data:${payload.mime_type};base64,${payload.audio_base64}`;
  return null;
}

const DEFAULT_LABEL = {
  voice: "Listen",
  mantra: "Chant with Āchārya",
  meditation: "Start Meditation",
};

export function AcharyaAudioPlayer({
  mode = "voice",
  text,
  mantra,
  label,
  src,
  autoPlay = false,
  token,
  minutes,
  className = "",
}) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const effectiveSrc = useMemo(() => src || asAudioSrc(payload), [src, payload]);
  const effectiveLabel = label || DEFAULT_LABEL[mode] || "Play";

  useEffect(() => {
    if (!autoPlay || !effectiveSrc) return;
    const el = document.getElementById(`acharya-audio-${mode}-${hashKey(text || mantra || src || "")}`);
    el?.play?.().catch(() => {});
  }, [autoPlay, effectiveSrc, mode, text, mantra, src]);

  const doFetch = async () => {
    setErr("");
    setLoading(true);
    try {
      const input = mode === "mantra" ? (mantra || "om") : (text || "");
      const data = await fetchAcharyaAudio(input, mode, token, minutes);
      setPayload(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.error || e?.message || "Audio failed");
    }
    setLoading(false);
  };

  const id = useMemo(() => `acharya-audio-${mode}-${hashKey(text || mantra || src || "")}`, [mode, text, mantra, src]);
  const showButton = !(src && !text && !mantra && !label);

  return (
    <div className={className} data-testid="acharya-audio-wrap">
      {showButton ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={doFetch}
          disabled={loading || (mode === "voice" && !text?.trim())}
          data-testid={
            mode === "voice"
              ? "acharya-listen"
              : mode === "mantra"
                ? "acharya-chant"
                : mode === "meditation"
                  ? "acharya-meditate"
                  : "acharya-audio-btn"
          }
        >
          {loading ? "Loading…" : effectiveLabel}
        </Button>
      ) : null}

      {err ? (
        <p className="text-xs text-destructive mt-2" data-testid="acharya-audio-error">
          {err}
        </p>
      ) : null}

      {effectiveSrc ? (
        <div className="mt-2" data-testid="acharya-audio-player">
          <audio key={effectiveSrc} id={id} controls preload="auto" src={effectiveSrc} className="w-full" />
        </div>
      ) : null}
    </div>
  );
}

function hashKey(s) {
  // small stable hash for DOM ids
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return String(h);
}

